/**
 * CLN-6.9 — elevated rights on tickets, booking links and design requests.
 *
 * Four services each decided "is this person internal staff" by testing the email
 * address against one company's domain. Measured live 2026-08-19, that had two
 * separate consequences, and the second one is the reason this file is a module
 * rather than four inline edits:
 *
 *   ESCALATION. 58 users hold such an address across 2 accounts (Solerro and
 *   Joules). Only 20 of them are Admin or Superadmin, so 38 were silently granted
 *   admin rights they do not hold — among them an Investor, a Marketer, a Site
 *   Surveyor and 2 Sales Reps. It is also account-blind: the 3 non-admins in the
 *   Joules account were admins THERE purely because of their address.
 *
 *   LOCKOUT. Nobody else could ever earn those rights by this path, whatever role
 *   they held. `designRequestService` was the pure case — see the note on
 *   `canUpdateDesignRequestStatus` below.
 *
 * ⚠️ THE POINT OF THIS FILE IS THAT THE TOKEN IS NAMED ONCE. `lib/effectiveRole.ts`
 * records what happens otherwise: "is this user an admin" acquired four spellings and
 * `check_user_role` became the fourth, which is where F-8 hid. A predicate per surface,
 * all in one file, is reviewable in a way that four scattered expressions are not.
 * `internalAccess.test.ts` additionally FAILS THE BUILD if the email-domain shape
 * reappears anywhere in `src`.
 *
 * The permissions are real and pre-existing; this file introduces none. Coverage
 * measured against `role_permissions` on 2026-08-19:
 *
 *   manage_tickets    9 roles — Admin, Agency Admin, Biz Dev Manager, Biz Dev Rep,
 *                     Commercial Sales Manager, Installer PM Manager, Internal PM,
 *                     Internal PM Manager, Superadmin
 *   manage_settings   8 roles — Admin, Agency Admin, Biz Dev Manager, Biz Dev Rep,
 *                     Finance, Internal PM, Internal PM Manager, Superadmin
 */

/**
 * A permission test, as `PermissionContext` exposes it.
 *
 * ⚠️ Passed in rather than read from a hook, because all four callers are plain
 * services with no React context. This follows `financeLayoutService.ts`'s
 * `deriveFinancePermissionContext`, which takes the same parameter for the same reason.
 */
export type HasPermission = (permission: string) => boolean;

/**
 * Fail closed. An absent `hasPermission` means the caller could not resolve
 * permissions, and an authorization check that cannot answer must deny. This is also
 * what `PermissionContext`'s own default value does while permissions are loading.
 */
function granted(hasPermission: HasPermission | null | undefined, permission: string): boolean {
  return typeof hasPermission === 'function' && hasPermission(permission) === true;
}

/**
 * May this person update a ticket they neither created nor are assigned to?
 *
 * ⚠️ This is the OVERRIDE, not the ordinary right. `ticketService.updateTicket` already
 * allows the creator and the assignee unconditionally, so this answers only the
 * "any ticket in the account" case. `manage_tickets` is the matching token — "Can
 * create, update, and delete tickets" — and it deliberately excludes Sales Manager,
 * Sales Rep, Finance, Team Owner, Investor, Marketer and Site Surveyor, which is
 * precisely the set the email check was escalating. Those roles keep `view_tickets`,
 * and Sales Manager keeps `edit_tickets`.
 */
export function canManageAnyTicket(hasPermission: HasPermission | null | undefined): boolean {
  return granted(hasPermission, 'manage_tickets');
}

/**
 * May this person see every booking link in the account, rather than only their own?
 *
 * `manage_settings` rather than a new token: booking links are configured from
 * Settings and no booking-specific permission exists. Allan's call, 2026-08-19.
 */
export function canViewAllBookingLinks(hasPermission: HasPermission | null | undefined): boolean {
  return granted(hasPermission, 'manage_settings');
}

/**
 * May this person move a design request's status?
 *
 * ⚠️ THIS REPLACED A ROLE-NAME ARRAY THAT COULD NEVER MATCH, so it widens access on
 * purpose. The old check was `isInternalUser || hasAllowedRole`, where `hasAllowedRole`
 * tested `user.roles?.includes(...)` and `user.role === ...` against a row selected as
 * `users.*`. `public.users` has exactly one role column, `role_id bigint` — no `role`,
 * no `roles` (verified against `information_schema.columns`, 2026-08-19). Both operands
 * were therefore always undefined and the array was dead on every code path, including
 * its `'Super Admin'` entry, which is not a role name here — the real one is
 * `'Superadmin'`.
 *
 * So the domain test was the ONLY live gate: a real Admin outside that domain could not
 * move a status, and in the Joules account the people who could were 4 address-holders
 * rather than its actual admins. `manage_settings` covers every role the dead array
 * named — Admin, Internal PM, Internal PM Manager, Biz Dev Rep — and, unlike the array,
 * actually reaches Superadmin.
 */
export function canUpdateDesignRequestStatus(hasPermission: HasPermission | null | undefined): boolean {
  return granted(hasPermission, 'manage_settings');
}
